import React from 'react';
import ContractUpload from './components/ContractUpload';
import PricingPlans from './components/PricingPlans';
import Header from './components/Header';
import Footer from './components/Footer';
import Results from './components/Results';
import { ResultsDemo } from './components/Results';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, Link } from 'react-router-dom';
import SeeItInAction from './components/SeeItInAction';

// Duplicate riskCategories for frontend use
const riskCategories = [
  { name: "Financial Risk", description: "unexpected costs, revenue loss, budget overruns" },
  { name: "Reputational Risk", description: "negative publicity, loss of trust, brand damage" },
  { name: "Legal/Compliance Risk", description: "lawsuits, fines, regulatory violations, audit failures" },
  { name: "Operational Risk", description: "process failures, bottlenecks, supply chain disruption, resource shortages" },
  { name: "Technical Risk", description: "security vulnerabilities, system failures, poor code quality, integration or compatibility issues, scalability limits" },
  { name: "Privacy/Data Risk", description: "data breaches, mishandling of sensitive information, data sovereignty issues" },
  { name: "Strategic Risk", description: "misalignment with goals, poor planning or forecasting, market disruption, poor innovation strategy" },
  { name: "People/Human Risk", description: "skills gaps, high turnover, insider threats, poor communication, resistance to change, toxic culture" },
  { name: "Dependency/Vendor Risk", description: "over-reliance on a single vendor, supplier lock-in, vendor failure" },
  { name: "Change Management Risk", description: "failed adoption of new systems/processes, poor transition planning" },
  { name: "Environmental Risk", description: "natural disasters, pandemics, climate impacts that disrupt operations" },
  { name: "Political/Geopolitical Risk", description: "trade restrictions, sanctions, unstable governments, regional conflict" },
  { name: "Ethical/Social Risk", description: "misuse of technology, discrimination, human rights concerns, community backlash" },
  { name: "Innovation/Obsolescence Risk", description: "products or tech becoming obsolete, failing to keep up with competitors" },
  { name: "Customer Risk", description: "over-reliance on key customers, churn, poor adoption or satisfaction" },
  { name: "Intellectual Property (IP) Risk", description: "IP theft, weak protection, patent disputes" },
  { name: "Contractual Risk", description: "ambiguous terms, gaps in SLAs, unclear obligations" },
  { name: "Quality Risk", description: "defective products, services not meeting standards/specs" },
  { name: "Cultural/Organizational Risk", description: "misaligned teams, poor morale, toxic workplace dragging down execution" },
];

function HowItWorks() {
  return (
    <div className="max-w-3xl mx-auto py-12">
      <h1 className="text-3xl font-bold mb-4">How It Works</h1>
      <ol className="list-decimal pl-6 text-lg text-gray-700 space-y-3">
        <li>Upload your legal document (PDF).</li>
        <li>Our AI analyzes the document and extracts key risks and summaries.</li>
        <li>Get a free preview with a risk score and summary of the first 3 pages.</li>
        <li>Pay once to unlock the full analysis and download a comprehensive PDF report.</li>
      </ol>
      <div className="my-10 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-xl font-bold text-blue-800 mb-3">What risks are checked?</h3>
        <ul className="list-disc pl-6 text-base text-gray-800 space-y-1">
          {riskCategories.map(cat => (
            <li key={cat.name}><strong>{cat.name}:</strong> {cat.description}</li>
          ))}
        </ul>
        <p className="mt-6 text-base text-gray-700">
          Have suggestions for other risks we should check? <a href="mailto:contactus@legalclarity.ai"  target="_blank" className="text-blue-600 underline">Contact us</a> and let us know!
        </p>
      </div>
      <SeeItInAction />
      <div className="mt-8 text-center">
        <Link to="/" className="text-blue-600 hover:underline">Back to Home</Link>
      </div>
    </div>
  );
}

function FAQ() {
  return (
    <div className="max-w-3xl mx-auto py-12">
      <h1 className="text-3xl font-bold mb-4">Frequently Asked Questions</h1>
      <div className="space-y-6 text-lg text-gray-700">
        <div>
          <h2 className="font-semibold text-xl mb-1">Is my document secure?</h2>
          <p>Your document is processed securely and is never shared. We use encrypted storage and delete files after processing.</p>
        </div>
        <div>
          <h2 className="font-semibold text-xl mb-1">What types of documents are supported?</h2>
          <p>We support most legal documents in PDF format, including contracts, agreements, and NDAs.</p>
        </div>
        <div>
          <h2 className="font-semibold text-xl mb-1">How accurate is the AI analysis?</h2>
          <p>Our AI is trained on thousands of legal documents, but results should always be reviewed by a qualified attorney.</p>
        </div>
        <div>
          <h2 className="font-semibold text-xl mb-1">How do I get the full analysis?</h2>
          <p>You can pay once to unlock the full summary and risk breakdown for your entire document.</p>
        </div>
        <div>
          <h2 className="font-semibold text-xl mb-1">Can I use this for business or enterprise?</h2>
          <p>Contact us at <a href="mailto:contactus@legalclarity.ai" target="_blank" className="text-blue-600 underline">contactus@legalclarity.ai</a> for enterprise solutions.</p>
        </div>
      </div>
      <div className="my-10 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-xl font-bold text-blue-800 mb-3">What risks are checked?</h3>
        <ul className="list-disc pl-6 text-base text-gray-800 space-y-1">
          {riskCategories.map(cat => (
            <li key={cat.name}><strong>{cat.name}:</strong> {cat.description}</li>
          ))}
        </ul>
        <p className="mt-6 text-base text-gray-700">
          Have suggestions for other risks we should check? <a href="mailto:contactus@legalclarity.ai" target="_blank" className="text-blue-600 underline">Contact us</a> and let us know!
        </p>
      </div>
      <div className="mt-8 text-center">
        <Link to="/" className="text-blue-600 hover:underline">Back to Home</Link>
      </div>
    </div>
  );
}

function ContactUs() {
  return (
    <div className="max-w-2xl mx-auto py-12 text-center">
      <h1 className="text-3xl font-bold mb-4">Contact Us</h1>
      <p className="text-lg text-gray-700 mb-6">Have a question or need support? Email us at:</p>
      <a href="mailto:contactus@legalclarity.ai" target="_blank" className="text-blue-600 underline text-xl">contactus@legalclarity.ai</a>
      <div className="mt-8">
        <Link to="/" className="text-blue-600 hover:underline">Back to Home</Link>
      </div>
    </div>
  );
}

function App() {
  const navigate = useNavigate();
  const seeItInActionId = process.env.REACT_APP_SEE_IT_IN_ACTION_RESULT_ID;
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <Routes>
          <Route path="/" element={
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                  AI Legal Document Summarizer
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Upload any legal document and get a plain-English summary with risk analysis
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
                  <p className="text-blue-800 font-semibold">
                    Get Started for Free: Upload your document and receive a full summary and risk score for up to 3 pages-no payment required.
                  </p>
                </div>
                <SeeItInAction />
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Simple Pricing</h3>
                  <div className="flex justify-center text-lg text-gray-700 mb-2">
                    <span>$9 per 20 pages</span>
                  </div>
                  <div className="text-gray-500 text-sm mb-4">No subscriptions. No hidden fees.</div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <h4 className="text-md font-semibold text-blue-800 mb-2">Example Pricing:</h4>
                    <ul className="text-gray-800 text-base space-y-1 text-left mx-auto max-w-md">
                      <li><strong>1 page:</strong> $9 (1 × $9)</li>
                      <li><strong>15 pages:</strong> $9 (1 × $9, covers up to 20 pages)</li>
                      <li><strong>32 pages:</strong> $18 (2 × $9, covers up to 40 pages)</li>
                      <li><strong>105 pages:</strong> $54 (6 × $9, covers up to 120 pages)</li>
                    </ul>
                    <div className="text-xs text-gray-500 mt-2">We always round up to the next 20 pages. For example, 21 pages = $18, 41 pages = $27, etc.</div>
                  </div>
                </div>
              </div>
              <ContractUpload onUploadComplete={result => {
                if (result && result.jobId) {
                  navigate(`/results/${result.jobId}`);
                }
              }} />
              <div className="mt-16">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Simple, Transparent Pricing
                  </h2>
                  <p className="text-gray-600">
                    Pay once, get your analysis. No subscriptions, no hidden fees.
                  </p>
                </div>
                <PricingPlans />
              </div>
            </div>
          } />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/upload/:planType" element={<UploadWithPlanType />} />
          <Route path="/results/:jobId" element={<Results />} />
          <Route path="/demo-results/:jobId" element={<ResultsDemo />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function UploadWithPlanType() {
  const { planType } = useParams();
  const navigate = useNavigate();
  const planSummaries = {
    free: {
      title: 'Get Started for Free',
      description: 'Full summary and risk score for up to 3 pages-no payment required.',
      features: [
        'Full summary (up to 3 pages)',
        'Risk score (up to 3 pages)',
        'No payment required',
      ],
      switchText: 'Need a full summary and risk analysis?',
      switchTo: 'quick_check',
      switchLabel: 'Upgrade to Quick Check',
    },
    quick_check: {
      title: 'Quick Check',
      description: 'Full summary and risk analysis for your entire document (billed at $9 per 20 pages).',
      features: [
        'Full summary',
        'Comprehensive risk analysis',
      ],
      switchText: 'Just want the free option?',
      switchTo: 'free',
      switchLabel: 'Get Started for Free',
    }
  };
  const plan = planSummaries[planType] || planSummaries['free'];
  return (
    <div className="max-w-2xl mx-auto py-12">
      <div className="mb-8 p-6 bg-white rounded-lg shadow text-center">
        <h2 className="text-2xl font-bold mb-2">{plan.title}</h2>
        <p className="text-gray-700 mb-4">{plan.description}</p>
        <ul className="mb-4 text-left inline-block">
          {plan.features.map((f, i) => (
            <li key={i} className="flex items-center mb-1">
              <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
              <span className="text-gray-800">{f}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4">
          <span className="text-gray-600 mr-2">{plan.switchText}</span>
          <button className="text-blue-600 hover:underline font-medium" onClick={() => navigate(`/upload/${plan.switchTo}`)}>{plan.switchLabel}</button>
        </div>
      </div>
      <ContractUpload
        onUploadComplete={result => {
          if (result && result.jobId) {
            navigate(`/results/${result.jobId}`);
          }
        }}
        planType={planType || 'free'}
      />
    </div>
  );
}

export default function AppWithRouter() {
  return (
    <Router>
      <App />
    </Router>
  );
} 